/**
 * Owns one MediaRecorder run from start to flushed blob.
 *
 * Two lifecycle details are easy to get wrong when the recorder is driven
 * directly from component code, and both corrupt the output file:
 *
 *   1. `MediaRecorder.stop()` is asynchronous. The final `dataavailable` fires
 *      on a later task, so a handler that appends into one shared buffer will
 *      drop the tail of recording N into the front of recording N+1 if the user
 *      restarts quickly. The resulting Blob contains two independent WebM
 *      segments; players decode the first one and then stall — a video that
 *      "freezes after a few seconds". Each run here owns its own buffer, keyed
 *      by session id, so a late chunk can only ever land in its own recording.
 *   2. Tearing down the source tracks immediately after `stop()` can truncate
 *      that same final flush. Release happens only once the recorder has
 *      actually finished.
 */

export interface RecorderLike {
  readonly state: string;
  readonly mimeType?: string;
  start(timesliceMs?: number): void;
  stop(): void;
  ondataavailable: ((event: BlobEvent) => void) | null;
  onstop: ((event: Event) => void) | null;
  onerror: ((event: Event) => void) | null;
}

export interface RecordingSessionDeps {
  createRecorder(
    stream: MediaStream,
    options: MediaRecorderOptions,
  ): RecorderLike;
  isTypeSupported(mimeType: string): boolean;
  /** Milliseconds to wait for `onstop` before giving up on the flush. */
  stopTimeoutMs?: number;
  setTimer?: (callback: () => void, delayMs: number) => number;
  clearTimer?: (handle: number) => void;
}

export interface StartRecordingParams {
  stream: MediaStream;
  videoBitsPerSecond: number;
  timesliceMs?: number;
  /** Ordered codec preferences; the first supported one wins. */
  codecPreferences?: string[];
  /** Runs after the recorder has fully flushed — release capture resources here. */
  onRelease?: () => void;
  onError?: (error: DOMException | Event) => void;
}

export interface RecordingResult {
  sessionId: number;
  chunks: BlobPart[];
  mimeType: string;
  /** Wall-clock length of the run; the WebM header does not carry one. */
  durationMs: number;
}

export type RecordingSessionState = "idle" | "recording";

export const RECORDING_CODEC_PREFERENCES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm;codecs=vp9",
  "video/webm;codecs=vp8",
  "video/webm",
];

export interface RecordingSession {
  readonly state: RecordingSessionState;
  readonly sessionId: number;
  /** How many previous runs are still waiting on their final flush. */
  readonly flushingCount: number;
  start(params: StartRecordingParams): void;
  stop(): Promise<RecordingResult | null>;
}

interface ActiveRun {
  id: number;
  recorder: RecorderLike;
  chunks: BlobPart[];
  mimeType: string;
  startedAt: number;
  onRelease?: () => void;
  settle: (result: RecordingResult) => void;
  settled: boolean;
  timeoutHandle: number | null;
}

const selectMimeType = (
  candidates: string[],
  isTypeSupported: (mimeType: string) => boolean,
): string | undefined => candidates.find((candidate) => isTypeSupported(candidate));

export const createRecordingSession = (
  deps: RecordingSessionDeps,
  now: () => number = () => Date.now(),
): RecordingSession => {
  const setTimer = deps.setTimer ?? ((cb, ms) => window.setTimeout(cb, ms));
  const clearTimer = deps.clearTimer ?? ((h) => window.clearTimeout(h));
  const stopTimeoutMs = deps.stopTimeoutMs ?? 3000;

  let nextId = 0;
  let run: ActiveRun | null = null;
  // Runs that have been stopped but whose final flush has not arrived yet. They
  // stay here, each with its own chunk buffer, so a new recording can begin
  // immediately without the old tail leaking into it.
  const flushing = new Set<ActiveRun>();

  const finish = (target: ActiveRun) => {
    if (target.settled) return;
    target.settled = true;
    if (target.timeoutHandle !== null) {
      clearTimer(target.timeoutHandle);
      target.timeoutHandle = null;
    }
    flushing.delete(target);
    // Only release capture resources once the recorder has flushed.
    target.onRelease?.();
    target.settle({
      sessionId: target.id,
      chunks: target.chunks,
      mimeType: target.mimeType,
      durationMs: Math.max(0, now() - target.startedAt),
    });
  };

  return {
    get state() {
      return run ? "recording" : "idle";
    },
    get sessionId() {
      return run?.id ?? nextId;
    },
    get flushingCount() {
      return flushing.size;
    },

    start(params: StartRecordingParams) {
      if (run) {
        throw new Error("A recording is already in progress.");
      }

      const codecs = params.codecPreferences ?? RECORDING_CODEC_PREFERENCES;
      const mimeType = selectMimeType(codecs, deps.isTypeSupported);
      const options: MediaRecorderOptions = {
        videoBitsPerSecond: params.videoBitsPerSecond,
        ...(mimeType ? { mimeType } : {}),
      };

      let recorder: RecorderLike;
      try {
        recorder = deps.createRecorder(params.stream, options);
      } catch {
        // Some browsers reject the options object but accept the defaults.
        recorder = deps.createRecorder(params.stream, {});
      }

      const id = ++nextId;
      const current: ActiveRun = {
        id,
        recorder,
        chunks: [],
        mimeType: recorder.mimeType || mimeType || "video/webm",
        startedAt: now(),
        onRelease: params.onRelease,
        settle: () => {},
        settled: false,
        timeoutHandle: null,
      };

      // Captures `current` rather than reading the live run, so a late flush
      // from this recorder can never contaminate a subsequent recording.
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          current.chunks.push(event.data);
        }
      };
      recorder.onstop = () => finish(current);
      recorder.onerror = (event) => {
        const error = (event as Event & { error?: DOMException }).error ?? event;
        params.onError?.(error);
        finish(current);
      };

      run = current;
      try {
        recorder.start(params.timesliceMs);
      } catch (error) {
        run = null;
        throw error;
      }
    },

    stop() {
      const current = run;
      if (!current) return Promise.resolve(null);

      // Hand the run off to the flush queue right away so the UI can start a
      // new take without waiting on the browser's deferred final chunk.
      run = null;
      flushing.add(current);

      const settled = new Promise<RecordingResult>((resolve) => {
        current.settle = resolve;
      });

      // Safety net: if `onstop` never arrives we still release resources and
      // hand back whatever was captured rather than hanging forever.
      current.timeoutHandle = setTimer(() => {
        current.timeoutHandle = null;
        finish(current);
      }, stopTimeoutMs);

      try {
        current.recorder.stop();
      } catch {
        finish(current);
      }

      return settled;
    },
  };
};
